import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalTranslations from '../translations/portal';
import axios from 'axios';
import { Compass, CheckCircle, X, RotateCcw } from 'lucide-react';
import ReadinessScore from '../components/ReadinessScore';
import HealthMonitor from '../components/HealthMonitor';
import SmartNotifications from '../components/SmartNotifications';
import OnboardingFlow from '../components/OnboardingFlow';
import CrossToolIntelligence from '../components/CrossToolIntelligence';
import EmmaInsightsWidget from '../components/EmmaInsightsWidget';
import WelcomeTour from '../components/WelcomeTour';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const Portal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const t = portalTranslations[lang];
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [profileToast, setProfileToast] = useState(false);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (location.state?.profileCompleted) {
      setProfileToast(true);
      window.history.replaceState({}, '');
      const timer = setTimeout(() => setProfileToast(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  useEffect(() => {
    const checkOnboarding = async () => {
      if (!token) { setCheckingOnboarding(false); return; }
      try {
        const res = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        });
        if (!res.data.completed) { setShowOnboarding(true); }
        else {
          // Onboarding done — check welcome tour
          try {
            const tourRes = await axios.get(`${API}/auth/tour-status`, {
              headers: { Authorization: `Bearer ${token}` }, timeout: 5000
            });
            if (!tourRes.data.has_seen_tour) setShowTour(true);
          } catch (_) {}
        }
      } catch (e) { console.error('Onboarding check failed:', e); }
      finally { setCheckingOnboarding(false); }
    };
    checkOnboarding();
  }, [token]);

  if (checkingOnboarding) {
    return (
      <div className="portal-home" data-testid="portal-home">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingFlow onComplete={() => {
      setShowOnboarding(false);
      // After onboarding, show tour
      (async () => {
        try {
          const tourRes = await axios.get(`${API}/auth/tour-status`, {
            headers: { Authorization: `Bearer ${token}` }, timeout: 5000
          });
          if (!tourRes.data.has_seen_tour) setShowTour(true);
        } catch (_) {}
      })();
    }} />;
  }

  const completeTour = async () => {
    setShowTour(false);
    try {
      await axios.post(`${API}/auth/tour-completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) {}
  };

  const restartTour = () => setShowTour(true);

  return (
    <div className="portal-home" data-testid="portal-home">
      {showTour && <WelcomeTour onComplete={completeTour} />}

      <div className="portal-welcome">
        <h2 className="heading-1" data-testid="portal-welcome">
          {t.welcome.title(user?.full_name?.split(' ')[0] || 'there')}
        </h2>
        <p className="body-large text-secondary">{t.welcome.subtitle}</p>
        <button onClick={restartTour} data-testid="restart-tour-btn" style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          marginTop: '0.5rem', padding: '0.35rem 0.75rem', fontSize: '0.8rem',
          background: 'none', border: '1px solid var(--border-light)', borderRadius: 6,
          color: 'var(--text-secondary)', cursor: 'pointer',
        }}>
          <RotateCcw size={13} /> {lang === 'nl' ? 'Tour opnieuw bekijken' : 'Replay tour'}
        </button>
      </div>

      {/* Profile completed toast */}
      {profileToast && (
        <div data-testid="profile-toast" style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.875rem 1.25rem', marginBottom: '1.5rem',
          background: '#16202C', border: '1px solid #bbf7d0', borderRadius: '10px',
          animation: 'fadeIn 0.3s ease-out',
        }}>
          <CheckCircle size={20} style={{ color: '#16a34a', flexShrink: 0 }} />
          <span style={{ flex: 1, color: '#15803d', fontWeight: 500, fontSize: '0.925rem' }}>
            {lang === 'nl'
              ? 'Bedrijfsprofiel bijgewerkt — Emma kan nu betere analyses maken!'
              : 'Company profile updated — Emma can now provide better analyses!'}
          </span>
          <button onClick={() => setProfileToast(false)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
            color: '#16a34a', opacity: 0.7,
          }}><X size={16} /></button>
        </div>
      )}

      {/* Smart Notifications */}
      <SmartNotifications />

      {/* AI Compass Banner + Scores */}
      <div className="portal-top-row">
        <div className="portal-compass-banner" onClick={() => navigate('/portal/ai-tools')} data-testid="compass-banner">
          <div className="compass-banner-content">
            <Compass size={32} />
            <div>
              <h3 className="heading-2">{t.aiToolsTitle}</h3>
              <p className="body-medium text-secondary">{t.aiToolsSubtitle(16)}</p>
            </div>
          </div>
          <span className="compass-banner-arrow">&rarr;</span>
        </div>
        <ReadinessScore />
      </div>

      {/* Health Monitor & Cross-Tool Intelligence */}
      <div className="portal-bottom-row">
        <HealthMonitor />
        <CrossToolIntelligence />
      </div>
    </div>
  );
};

export default Portal;

